import type { LogEntry, LoggerMode, LoggerState } from './types';
import {
  loadEntries,
  addEntry,
  deleteEntries,
  clearEntries as clearStoredEntries,
  loadSettings,
  saveSettings,
  trimToLimit
} from '../storage/logger-store';
import { entriesToCsv } from '../utils/csv';
import { CooldownDeduper } from '../utils/deduper';
import { trimByLimit } from '../utils/rolling-window';
import { formatBuildInfo } from '../utils/build-info';

const DEDUP_WINDOW_MS = 1000;
const INFO_CLEAR_DELAY_MS = 2500;

interface NfcSession {
  reader: NDEFReader;
  controller: AbortController;
}

export function createLoggerApp(root: HTMLElement) {
  const app = new LoggerApp(root);
  void app.init();
  return app;
}

class LoggerApp {
  private state: LoggerState;
  private readonly root: HTMLElement;
  private nfcSession: NfcSession | null = null;
  private infoTimeout: number | null = null;
  private readonly deduper = new CooldownDeduper({ windowMs: DEDUP_WINDOW_MS });

  constructor(root: HTMLElement) {
    this.root = root;
    this.state = {
      mode: 'IDLE',
      entries: [],
      metrics: this.computeMetrics([]),
      settings: { maxEntries: 500 },
      nfcAvailability: 'UNKNOWN'
    };

    this.handleReading = this.handleReading.bind(this);
    this.handleNfcError = this.handleNfcError.bind(this);
  }

  async init(): Promise<void> {
    this.render();
    await this.loadInitialState();
    this.observeVisibility();
  }

  private async loadInitialState(): Promise<void> {
    try {
      const [entries, settings] = await Promise.all([loadEntries(), loadSettings()]);
      entries.sort((a, b) => a.id - b.id);
      this.state = {
        ...this.state,
        entries,
        metrics: this.computeMetrics(entries),
        settings
      };
      this.render();
    } catch (error) {
      console.error('Failed to restore state', error);
      this.updateState({
        nfcAvailability: 'ERROR',
        lastError: 'Failed to restore saved log entries. IndexedDB may be unavailable.'
      });
    }

    if (!('NDEFReader' in window)) {
      this.updateState({ nfcAvailability: 'UNSUPPORTED' });
    } else {
      this.updateState({ nfcAvailability: 'READY' });
    }
  }

  private observeVisibility(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.state.mode === 'SCANNING') {
        this.stopScan();
        this.showInfo('Scanning paused because the tab lost focus. Tap “Start scanning” to resume.');
      }
    });
  }

  private computeMetrics(entries: LogEntry[]) {
    const total = entries.length;
    const unique = new Set(entries.map((entry) => entry.uid)).size;
    const last = entries.at(-1) ?? null;
    return {
      total,
      unique,
      lastUid: last ? last.uid : null,
      lastSeen: last ? new Date(last.timestamp).toLocaleString() : null
    };
  }

  private updateState(next: Partial<LoggerState>): void {
    this.state = { ...this.state, ...next };
    this.render();
  }

  private setMode(mode: LoggerMode): void {
    this.updateState({ mode, lastError: undefined });
  }

  private render(): void {
    const { mode, entries, metrics, settings, lastError, infoMessage } = this.state;
    const isScanning = mode === 'SCANNING';
    const availabilityMessage = this.describeAvailability();

    const tableRows = entries
      .slice()
      .reverse()
      .map(
        (entry) => `
          <tr>
            <td>${entry.uid}</td>
            <td>${new Date(entry.timestamp).toLocaleString()}</td>
          </tr>
        `
      )
      .join('');

    this.root.innerHTML = `
      <div style="background: #fbbf24; color: #000; padding: 1rem; font-weight: bold; font-size: 1.1rem; text-align: center; margin-bottom: 1rem;">
        DEBUG: Mode=${mode} | NFC=${this.state.nfcAvailability} | Session=${this.nfcSession ? 'ACTIVE' : 'NULL'}
      </div>
      <section class="layout-header">
        <div>
          <h1>LINQS Tag Logger</h1>
          <p>Phase 1: capture NFC tag UIDs with debouncing and offline persistence.</p>
        </div>
        <div class="action-row">
          <button data-action="start" ${isScanning ? 'disabled' : ''}>Start scanning</button>
          <button data-action="stop" class="secondary" ${isScanning ? '' : 'disabled'}>Stop</button>
          <a class="button secondary" href="/batch.html">Open batch writer</a>
        </div>
      </section>
      ${availabilityMessage}
      ${lastError ? `<div class="status-banner error">${lastError}</div>` : ''}
      ${infoMessage ? `<div class="status-banner">${infoMessage}</div>` : ''}
      <form class="logger-settings" data-action="settings">
        <label>
          Max entries
          <input type="number" name="maxEntries" min="1" max="5000" value="${settings.maxEntries}" required />
        </label>
        <button type="submit" class="secondary">Update</button>
        <small class="helper">Oldest entries roll off automatically once the limit is reached.</small>
      </form>
      <div class="action-row">
        <button data-action="export" ${entries.length === 0 ? 'disabled' : ''}>Export CSV</button>
        <button data-action="clear" class="secondary" ${entries.length === 0 ? 'disabled' : ''}>Clear all</button>
      </div>
      <section class="metric-cards">
        <article class="metric-card">
          <h3>Total scans</h3>
          <p>${metrics.total}</p>
        </article>
        <article class="metric-card">
          <h3>Unique UIDs</h3>
          <p>${metrics.unique}</p>
        </article>
        <article class="metric-card">
          <h3>Last UID</h3>
          <p>${metrics.lastUid ?? '—'}</p>
        </article>
        <article class="metric-card">
          <h3>Last seen</h3>
          <p>${metrics.lastSeen ?? '—'}</p>
        </article>
      </section>
      ${entries.length > 0
        ? `<table>
            <thead>
              <tr>
                <th>UID</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>`
        : '<div class="empty-state">No tag reads yet. Tap "Start scanning" and bring a tag near the device.</div>'}
      <footer class="app-footer">
        <small>${formatBuildInfo()}</small>
      </footer>
    `;

    this.attachEventHandlers();
  }

  private describeAvailability(): string {
    switch (this.state.nfcAvailability) {
      case 'UNSUPPORTED':
        return '<div class="status-banner error">This device or browser does not support Web NFC. Try Chrome on a compatible Android device.</div>';
      case 'PERMISSION_DENIED':
        return '<div class="status-banner error">NFC permission was denied. Reload the page and grant access to continue.</div>';
      case 'ERROR':
        return '<div class="status-banner error">NFC is unavailable due to an unexpected error.</div>';
      case 'READY':
        return '<div class="status-banner">Web NFC is ready. Tap “Start scanning” to begin.</div>';
      default:
        return '';
    }
  }

  private attachEventHandlers(): void {
    const startButton = this.root.querySelector<HTMLButtonElement>('[data-action="start"]');
    startButton?.addEventListener('click', (event) => {
      event.preventDefault();
      void this.startScan();
    });

    const stopButton = this.root.querySelector<HTMLButtonElement>('[data-action="stop"]');
    stopButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.stopScan();
    });

    const exportButton = this.root.querySelector<HTMLButtonElement>('[data-action="export"]');
    exportButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.exportCsv();
    });

    const clearButton = this.root.querySelector<HTMLButtonElement>('[data-action="clear"]');
    clearButton?.addEventListener('click', (event) => {
      event.preventDefault();
      void this.clearAll();
    });

    const settingsForm = this.root.querySelector<HTMLFormElement>('form[data-action="settings"]');
    settingsForm?.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(settingsForm);
      const next = Number(data.get('maxEntries'));
      if (Number.isFinite(next) && next >= 1 && next <= 5000) {
        void this.updateMaxEntries(next);
      }
    });
  }

  private async startScan(): Promise<void> {
    if (!('NDEFReader' in window)) {
      this.updateState({ nfcAvailability: 'UNSUPPORTED' });
      return;
    }
    if (this.state.mode === 'SCANNING') {
      return;
    }

    try {
      const reader = new NDEFReader();
      const controller = new AbortController();

      // Attach stable handlers to enable proper teardown
      reader.addEventListener('reading', this.handleReading as EventListener);
      reader.addEventListener('readingerror', this.handleNfcError as EventListener);

      await reader.scan({ signal: controller.signal });
      this.nfcSession = { reader, controller };
      this.setMode('SCANNING');
      this.updateState({ nfcAvailability: 'READY', infoMessage: 'Scan started - hold tag near device' });
    } catch (error) {
      const domError = error as DOMException;
      if (domError?.name === 'NotAllowedError') {
        this.updateState({ nfcAvailability: 'PERMISSION_DENIED', lastError: 'NFC permission denied. Retry and approve the prompt.' });
      } else if (domError?.name === 'NotSupportedError') {
        this.updateState({ nfcAvailability: 'UNSUPPORTED', lastError: 'Web NFC is not supported on this device.' });
      } else {
        this.updateState({
          nfcAvailability: 'ERROR',
          lastError: domError?.message ?? 'Unable to start NFC scanning.'
        });
      }
      this.teardownNfcSession();
    }
  }

  private stopScan(): void {
    this.teardownNfcSession();
    this.setMode('IDLE');
    this.deduper.clear();
  }

  private teardownNfcSession(): void {
    if (this.nfcSession) {
      this.nfcSession.reader.removeEventListener('reading', this.handleReading as EventListener);
      this.nfcSession.reader.removeEventListener('readingerror', this.handleNfcError as EventListener);
      this.nfcSession.controller.abort();
    }
    this.nfcSession = null;
  }

  private async handleReading(event: Event): Promise<void> {
    const readingEvent = event as Event & { serialNumber?: string };
    const uid = (readingEvent.serialNumber && readingEvent.serialNumber.trim()) || '(unknown)';
    const now = Date.now();

    if (!this.deduper.shouldAccept(uid, now)) {
      this.showInfo('Duplicate tap ignored. Remove the tag before scanning again.');
      return;
    }

    const timestamp = new Date().toISOString();
    const entry = await addEntry({ uid, timestamp });
    const { retained, removed } = trimByLimit(
      [...this.state.entries, entry],
      this.state.settings.maxEntries
    );
    if (removed.length > 0) {
      await deleteEntries(removed.map((item) => item.id));
    }
    const entries = retained;

    const metrics = this.computeMetrics(entries);
    this.updateState({ entries, metrics, infoMessage: `Tag ${uid} logged.` });
    this.scheduleInfoClear();
  }

  private handleNfcError(event: Event): void {
    console.error('NFC error event', event);
    this.updateState({ lastError: 'An NFC error occurred during scanning.' });
  }

  private exportCsv(): void {
    if (this.state.entries.length === 0) {
      return;
    }
    const csv = entriesToCsv(this.state.entries);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `linqs-tag-logger-${new Date().toISOString()}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private async clearAll(): Promise<void> {
    if (!window.confirm('Clear all logged tag entries?')) {
      return;
    }
    await clearStoredEntries();
    this.updateState({ entries: [], metrics: this.computeMetrics([]) });
    this.showInfo('All entries cleared.');
    this.deduper.clear();
  }

  private async updateMaxEntries(next: number): Promise<void> {
    if (next === this.state.settings.maxEntries) {
      return;
    }
    const settings = { ...this.state.settings, maxEntries: Math.floor(next) };
    await saveSettings(settings);
    await trimToLimit(settings.maxEntries);
    const entries = (await loadEntries()).sort((a, b) => a.id - b.id);
    this.updateState({ settings, entries, metrics: this.computeMetrics(entries) });
  }

  private showInfo(message: string): void {
    this.updateState({ infoMessage: message });
    this.scheduleInfoClear();
  }

  private scheduleInfoClear(): void {
    if (this.infoTimeout) {
      window.clearTimeout(this.infoTimeout);
    }
    this.infoTimeout = window.setTimeout(() => {
      this.updateState({ infoMessage: undefined });
      this.infoTimeout = null;
    }, INFO_CLEAR_DELAY_MS);
  }
}
